import { useEffect, useRef, useState } from 'react';
import { DiffEditor } from '@monaco-editor/react';
import { avisoDeFerramenta, fraseDoPedido, ladosDoDiffUnificado } from '@remoto/shared';
import type { DecisaoPermissao, TarefaViva } from '@remoto/protocolo';
import { linguagemDe } from '../../editor/monaco';
import {
  IconeAbrir,
  IconeAgente,
  IconeAnterior,
  IconeAviso,
  IconeCirculo,
  IconeConfirmado,
  IconeEsperando,
  IconeFechado,
  IconeMinimizar,
  IconePonto,
  IconePreso,
  IconeProximo,
  IconeSolto,
  IconeTrocaDeModelo,
} from '../icones';
import { SeloAssinatura } from '../SeloAssinatura';
import { usarTemaDoEditor } from '../../estado/tema';
import type { ItemDeChat } from '../../sessoes/mensagens';
import { textoDoTurno } from '../../sessoes/custo-do-turno';
import {
  interpretarPedido,
  motorDoPedido,
  type CampoDoFormulario,
  type CartaoDePedido,
  type PerguntaDoCartao,
} from '../../sessoes/pedidos';
import { CaixaDeMarcar, CampoDeEscolha } from '../campos';
import { TextoMarkdown } from './TextoMarkdown';
import { ImagemDaFerramenta } from './ImagemDaConversa';
import { AnexoDaSessao } from './AnexoDaSessao';
import { classeConhecida, hashCurto } from '../../toolkit/regras';
import { SeloDaClasse, SeloDoEstado } from '../../toolkit/selos';

export interface Pedido {
  id: string;
  tipo?: string;
  /**
   * O motor da sessão dona do pedido. Ausente = CLAUDE (`motorDoPedido`).
   * A API não o manda na lista de pedidos: quem o preenche é `usarConversa`,
   * com o motor da sessão.
   */
  motor?: string;
  ferramenta: string;
  entrada: Record<string, unknown>;
  sugestoes?: unknown[] | null;
  titulo?: string | null;
  estado: string;
  criadoEm: string;
}

function contarLinhas(antes: string, depois: string): { adicoes: number; remocoes: number } {
  return {
    adicoes: depois.length > 0 ? depois.split('\n').length : 0,
    remocoes: antes.length > 0 ? antes.split('\n').length : 0,
  };
}

/**
 * Uma edição que a sessão fez, com a comparação de verdade.
 *
 * Recolhido por padrão: numa conversa longa, meia dúzia de diffs abertos
 * empurram a conversa para fora da tela. Aberto, é o mesmo `DiffEditor` do
 * editor — ver a mudança no mesmo lugar em que se vê o arquivo evita ter de
 * traduzir de cabeça entre duas formas de mostrar a mesma coisa.
 */
function CartaoDeEdicao({
  item,
  aoAbrirArquivo,
  diffPreso = false,
  aoPrenderDiff,
}: {
  item: Extract<ItemDeChat, { tipo: 'edicao' }>;
  aoAbrirArquivo?: (caminho: string) => void;
  /** Preferência: todo cartão de edição já nasce com a comparação aberta. */
  diffPreso?: boolean;
  aoPrenderDiff?: (preso: boolean) => void;
}): React.JSX.Element {
  const [aberto, setAberto] = useState(diffPreso);
  // Fora do JSX de propósito: dentro do `aberto ?` seria um hook condicional.
  const temaDoEditor = usarTemaDoEditor();

  // O cadeado vale para os cartões todos, inclusive os que já estão na tela:
  // fixar a escolha e ter de abrir um por um seria fixar quase nada.
  useEffect(() => setAberto(diffPreso), [diffPreso]);

  /**
   * A altura vem do que o editor de fato desenhou, não da conta de linhas.
   *
   * Contar linhas do texto errava sempre para mais: o diff recolhe o que não
   * mudou, então uma remoção de quarenta linhas virava um caixote com trinta
   * linhas vazias embaixo. O primeiro palpite é só para o editor nascer com um
   * tamanho plausível; logo em seguida ele diz a altura de verdade.
   */
  const [altura, setAltura] = useState(160);

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40">
      <div className="flex items-center gap-2 px-3 py-2 text-xs">
        <button
          onClick={() => setAberto((v) => !v)}
          aria-expanded={aberto}
          aria-label={aberto ? 'Esconder a comparação' : 'Mostrar a comparação'}
          title={aberto ? 'Esconder a comparação' : 'Mostrar a comparação'}
          className="shrink-0 text-zinc-500 transition hover:text-zinc-200 pointer-coarse:-my-2 pointer-coarse:flex pointer-coarse:h-11 pointer-coarse:w-8 pointer-coarse:items-center pointer-coarse:justify-center"
        >
          {aberto ? <IconeAbrir tamanho={14} /> : <IconeFechado tamanho={14} />}
        </button>
        <span className="shrink-0 text-zinc-500">{item.criacao ? 'criou' : 'editou'}</span>
        {aoAbrirArquivo ? (
          <button
            onClick={() => aoAbrirArquivo(item.arquivo)}
            title={'Abrir ' + item.arquivo}
            className="min-w-0 flex-1 truncate text-left font-mono text-zinc-300 hover:text-emerald-300 hover:underline"
          >
            {item.arquivo}
          </button>
        ) : (
          <span className="min-w-0 flex-1 truncate font-mono text-zinc-300">{item.arquivo}</span>
        )}
        <span className="shrink-0 tabular-nums">
          <span className="text-emerald-400">+{item.adicoes}</span>{' '}
          <span className="text-red-400">-{item.remocoes}</span>
        </span>
        {aoPrenderDiff ? (
          <button
            onClick={() => aoPrenderDiff(!diffPreso)}
            aria-pressed={diffPreso}
            aria-label={diffPreso ? 'Soltar a comparação' : 'Sempre abrir a comparação'}
            title={
              diffPreso
                ? 'a comparação abre sozinha em toda edição — clique para soltar'
                : 'sempre abrir a comparação'
            }
            className={
              'shrink-0 transition ' +
              'pointer-coarse:-my-2 pointer-coarse:flex pointer-coarse:h-11 pointer-coarse:w-8 pointer-coarse:items-center pointer-coarse:justify-center ' +
              // No dedo o repouso já é legível: `zinc-700` sobre `zinc-950` dá
              // ~2:1, e o `hover` que o resgatava não existe no toque.
              (diffPreso ? 'text-emerald-400' : 'text-zinc-700 pointer-coarse:text-zinc-500 hover:text-zinc-400')
            }
          >
            {diffPreso ? <IconePreso tamanho={14} /> : <IconeSolto tamanho={14} />}
          </button>
        ) : null}
      </div>

      {aberto ? (
        <div className="border-t border-zinc-800" style={{ height: altura }}>
          <DiffEditor
            original={item.antes}
            modified={item.depois}
            // Sem caminho de modelo os dois lados caem na mesma URI e o Monaco
            // compara o texto com ele mesmo: o cartão mostrava o código
            // colorido e nenhuma diferença. A chave leva o arquivo e a chave do
            // item porque a mesma edição pode aparecer duas vezes na conversa.
            originalModelPath={`edicao-antes/${item.chave}/${item.arquivo}`}
            modifiedModelPath={`edicao-depois/${item.chave}/${item.arquivo}`}
            language={linguagemDe(item.arquivo)}
            theme={temaDoEditor}
            onMount={(editor) => {
              const modificado = editor.getModifiedEditor();
              const medir = (): void =>
                setAltura(Math.min(420, Math.max(72, modificado.getContentHeight() + 12)));
              medir();
              modificado.onDidContentSizeChange(medir);
            }}
            options={{
              readOnly: true,
              // Lado a lado num painel estreito vira duas colunas ilegíveis;
              // em linha, com as duas numerações no vão, é o mesmo desenho que
              // o VS Code usa — e cabe em qualquer largura.
              renderSideBySide: false,
              lineNumbers: 'on',
              renderIndicators: true,
              diffWordWrap: 'on',
              // Trecho longo com uma linha trocada no meio: o Monaco recolhe o
              // que não mudou, como no editor de verdade.
              hideUnchangedRegions: { enabled: true, contextLineCount: 2, minimumLineCount: 4 },
              fontSize: 12,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              renderOverviewRuler: false,
              folding: false,
              // A roda do mouse volta para a conversa quando o diff chega ao
              // fim. Sem isto o cartão engole a rolagem e a conversa trava
              // toda vez que o ponteiro passa por cima de uma edição.
              scrollbar: { alwaysConsumeMouseWheel: false },
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Os estados que o daemon relata, em português. */
const ESTADO_DO_SUBAGENTE: Record<string, string> = {
  concluida: 'concluído',
  falhou: 'falhou',
  parada: 'parado',
  pausada: 'pausado',
};

/**
 * O que dizer na linha do subagente, em duas palavras.
 *
 * A tarefa viva do stream é a fonte mais fresca, mas ela some quando a sessão
 * sai do ar — e o transcript continua ali. Sem tarefa, o próprio grupo já sabe
 * responder: fechado quer dizer que o resultado chegou.
 */
function estadoDoSubagente(
  item: Extract<ItemDeChat, { tipo: 'subagente' }>,
  tarefa?: TarefaViva,
): string {
  const relatado = tarefa ? ESTADO_DO_SUBAGENTE[tarefa.estado] : undefined;
  if (relatado) return relatado;
  if (item.aberto) return 'trabalhando';
  return item.itens.length === 1 ? '1 passo' : `${item.itens.length} passos`;
}

/**
 * A partir de onde uma mensagem sua deixa de ser recado e vira documento.
 *
 * Mil e duzentos caracteres são umas quinze linhas na largura da conversa: o
 * bastante para um parágrafo comprido passar inteiro, e pouco o bastante para
 * segurar o que o `/compact` cola ali. Nem toda mensagem grande é do compact —
 * texto colado de um e-mail, uma pilha de log — e todas elas se leem melhor
 * dobradas, com o botão de abrir do lado.
 */
const TEXTO_LONGO = 1_200;

/**
 * Uma bolha de texto, dobrada quando o texto é grande demais para ser recado.
 *
 * O caso que motivou: rodar `/compact` gera uma mensagem SUA com o resumo
 * inteiro da conversa — você mandando um documento para você mesmo. Ela
 * chegava aberta, com dezenas de milhares de caracteres, e empurrava a conversa
 * de verdade para longe.
 *
 * Só vale para o que **você** mandou. A resposta do Claude fica inteira: é ela
 * que se está esperando ler, e dobrar o que a pessoa pediu seria esconder o
 * trabalho no exato momento em que ele chega.
 */
function BolhaDeTexto({
  item,
  cwd,
  sessaoId,
  aoAbrirArquivo,
  aoIrParaSessao,
}: {
  item: Extract<ItemDeChat, { tipo: 'texto' }>;
  cwd?: string;
  sessaoId?: string;
  aoAbrirArquivo?: (caminho: string) => void;
  /** Link `[título](/sessoes/<id>)` troca de conversa sem recarregar (F13). */
  aoIrParaSessao?: (sessaoId: string) => void;
}): React.JSX.Element {
  const meu = item.autor === 'voce';
  const dobravel = meu && item.texto.length > TEXTO_LONGO;
  const [aberto, setAberto] = useState(false);
  const mostrando = dobravel && !aberto ? item.texto.slice(0, TEXTO_LONGO) + '…' : item.texto;

  return (
    <div className={'flex items-start gap-1.5 ' + (meu ? 'justify-end' : 'justify-start')}>
      {/* Já saiu daqui, mas a sessão ainda não leu: fica pálida até virar
          conversa de verdade, que é quando o Claude Code a tira da fila. */}
      {item.naFila ? (
        <span className="mt-2.5 shrink-0 text-zinc-600" title="esperando a vez">
          <IconeEsperando tamanho={14} />
        </span>
      ) : null}
      <div
        className={
          'min-w-0 max-w-[85ch] rounded-xl px-4 py-2.5 text-sm ' +
          (item.naFila ? 'opacity-60 ' : '') +
          (meu ? 'bg-zinc-100 text-zinc-900' : 'bg-zinc-900 text-zinc-200 ring-1 ring-zinc-800')
        }
      >
        {/*
          Quem mandou, no pedido literal do dono (2026-09-21): negrito, fonte
          MENOR que a do corpo (`text-xs` contra o `text-sm` da bolha), logo no
          começo da mensagem.

          `block` + `truncate` num pai `min-w-0`: um nome comprido corta com
          reticências em vez de esticar a bolha. Medir `scrollWidth` não pegaria
          o estouro — sob `overflow-hidden` o que passa é cortado, sem barra
          nenhuma avisando —, e foi esse o defeito da tela de contas.
        */}
        {item.autoria ? (
          <span
            data-testid="autoria"
            className={
              'mb-0.5 block truncate text-xs font-semibold ' +
              (meu ? 'text-zinc-600' : 'text-zinc-400')
            }
          >
            {item.autoria}
          </span>
        ) : null}
        <TextoMarkdown
          texto={mostrando}
          claro={meu}
          cwd={cwd}
          sessaoId={sessaoId}
          aoAbrirArquivo={aoAbrirArquivo}
          aoIrParaSessao={aoIrParaSessao}
        />
        {dobravel ? (
          <button
            type="button"
            onClick={() => setAberto((v) => !v)}
            className="mt-1 text-xs text-zinc-500 underline-offset-2 hover:text-zinc-700 hover:underline"
          >
            {aberto
              ? 'Mostrar menos'
              : `Mostrar tudo (${Math.round(item.texto.length / 1000)} mil caracteres)`}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Uma mensagem da conversa.
 *
 * `aoAbrirArquivo` liga as duas metades da tela: clicar no arquivo que a
 * sessão editou abre ele numa aba, em vez de deixar a pessoa procurar na
 * árvore o que já está escrito ali na frente.
 */
export function Bolha({
  item,
  aoAbrirArquivo,
  aoIrParaSessao,
  cwd,
  sessaoId,
  maquinaId,
  diffPreso,
  aoPrenderDiff,
  tarefas,
  aoPararTarefa,
  aoAbrirAgente,
}: {
  item: ItemDeChat;
  aoAbrirArquivo?: (caminho: string) => void;
  /** Link `[título](/sessoes/<id>)` troca de conversa sem recarregar (F13). */
  aoIrParaSessao?: (sessaoId: string) => void;
  /** A pasta da sessao, para link relativo virar arquivo. */
  cwd?: string;
  /** A conversa, para o caminho de um anexo virar a imagem dele. */
  sessaoId?: string;
  /** A máquina, para o relay servir imagem de arquivo do projeto. */
  maquinaId?: string;
  diffPreso?: boolean;
  aoPrenderDiff?: (preso: boolean) => void;
  /** Subagentes vivos do stream; é deles que vem o id para poder parar. */
  tarefas?: TarefaViva[];
  aoPararTarefa?: (tarefaId: string) => void;
  /** Abre a tela de agentes naquele subagente. */
  aoAbrirAgente?: (chave: string) => void;
}): React.JSX.Element {
  if (item.tipo === 'texto') {
    return (
      <BolhaDeTexto
        item={item}
        cwd={cwd}
        sessaoId={sessaoId}
        {...(aoAbrirArquivo ? { aoAbrirArquivo } : {})}
        {...(aoIrParaSessao ? { aoIrParaSessao } : {})}
      />
    );
  }

  if (item.tipo === 'pensamento') {
    return (
      <details className="rounded-lg border border-zinc-800/70 bg-zinc-900/20 px-3 py-2 text-xs">
        <summary className="cursor-pointer text-zinc-600 hover:text-zinc-400">pensamento</summary>
        <div className="mt-2 border-l-2 border-zinc-800 pl-3 text-zinc-500 italic">
          <TextoMarkdown
            texto={item.texto}
            cwd={cwd}
            sessaoId={sessaoId}
            aoAbrirArquivo={aoAbrirArquivo}
            aoIrParaSessao={aoIrParaSessao}
          />
        </div>
      </details>
    );
  }

  if (item.tipo === 'edicao') {
    return (
      <CartaoDeEdicao
        item={item}
        aoAbrirArquivo={aoAbrirArquivo}
        diffPreso={diffPreso}
        aoPrenderDiff={aoPrenderDiff}
      />
    );
  }

  if (item.tipo === 'tarefas') {
    const feitas = item.tarefas.filter((t) => t.estado === 'completed').length;
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/30 px-3 py-2 text-xs">
        <p className="mb-1.5 text-zinc-500">
          Tarefas · {feitas} de {item.tarefas.length}
        </p>
        <ul className="space-y-1">
          {item.tarefas.map((tarefa, i) => (
            <li key={i} className="flex items-start gap-2">
              <span
                aria-hidden
                className={
                  'mt-0.5 flex shrink-0 ' +
                  (tarefa.estado === 'completed'
                    ? 'text-emerald-500'
                    : tarefa.estado === 'in_progress'
                      ? 'text-sky-400'
                      : 'text-zinc-600')
                }
              >
                {tarefa.estado === 'completed' ? (
                  <IconeConfirmado tamanho={14} />
                ) : tarefa.estado === 'in_progress' ? (
                  <IconePonto tamanho={14} />
                ) : (
                  <IconeCirculo tamanho={14} />
                )}
              </span>
              <span
                className={
                  tarefa.estado === 'completed'
                    ? 'text-zinc-600 line-through'
                    : tarefa.estado === 'in_progress'
                      ? 'text-zinc-100'
                      : 'text-zinc-400'
                }
              >
                {tarefa.estado === 'in_progress' && tarefa.ativo ? tarefa.ativo : tarefa.texto}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (item.tipo === 'subagente') {
    // A tarefa viva casa pelo bloco `tool_use` do Task: é dela que vem o
    // `task_id`, sem o qual não há como parar UM subagente.
    const tarefa = tarefas?.find((t) => t.toolUseId && t.toolUseId === item.toolUseId);
    const rodando = item.aberto && (tarefa?.estado ?? 'rodando') === 'rodando';

    /*
      Uma linha, e só. Os passos e a saída do subagente vivem na tela de
      agentes.

      Antes o cartão trazia tudo dentro, dobrado: vinte e quatro passos e
      quatrocentas linhas de resultado escondidos atrás de um `<details>` no
      meio da conversa. Dobrado não é o mesmo que fora do caminho — quem abria
      para conferir um passo perdia o fio da conversa, e quem não abria
      carregava o peso mesmo assim. O trabalho do subagente é uma sessão
      paralela; o lugar dele é uma tela paralela, e na conversa fica o recibo.
    */
    return (
      <div className="flex items-center gap-1.5 rounded-lg border border-violet-900/60 bg-violet-950/10 px-3 py-2 text-xs pointer-coarse:min-h-11">
        <span className="shrink-0 text-violet-300" aria-hidden>
          <IconeAgente tamanho={14} />
        </span>
        <span className="shrink-0 text-violet-300">subagente</span>
        {item.subtipo ? (
          <span className="shrink-0 rounded bg-violet-900/50 px-1 text-[10px] text-violet-300">
            {item.subtipo}
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate text-zinc-400">{item.descricao}</span>
        <span className="shrink-0 text-zinc-500">{estadoDoSubagente(item, tarefa)}</span>
        {rodando ? (
          <span
            className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-violet-400"
            title="trabalhando"
          />
        ) : null}
        {rodando && tarefa && aoPararTarefa ? (
          <button
            onClick={() => aoPararTarefa(tarefa.id)}
            title="Parar só este subagente, sem interromper a sessão"
            className="shrink-0 rounded border border-zinc-700 px-1.5 py-0.5 text-[11px] text-zinc-400 hover:border-red-700 hover:text-red-300"
          >
            parar
          </button>
        ) : null}
        {aoAbrirAgente ? (
          <button
            onClick={() => aoAbrirAgente(item.chave)}
            title="Ver o que este subagente fez"
            className="shrink-0 rounded border border-violet-800 px-2 py-0.5 text-[11px] text-violet-200 transition hover:bg-violet-900/40"
          >
            Abrir
          </button>
        ) : null}
      </div>
    );
  }

  if (item.tipo === 'comando') {
    // A saída de /compact e amigos era apagada da tela; agora é um cartão
    // dobrável, no mesmo desenho das ferramentas.
    return (
      <details className="group rounded-lg border border-zinc-800/70 bg-zinc-900/20 px-3 py-2 text-xs">
        <summary className="flex cursor-pointer items-baseline gap-1.5 pointer-coarse:min-h-11 pointer-coarse:items-center">
          {/* O <details> abre e fecha por CSS, sem estado no React: então os
              dois ícones ficam montados e o `group-open` esconde um de cada
              vez. Um ícone só, virado por classe, ficaria parado no fechado. */}
          <IconeFechado tamanho={14} className="self-center text-zinc-600 group-open:hidden" />
          <IconeAbrir tamanho={14} className="hidden self-center text-zinc-600 group-open:block" />
          <span className="shrink-0 font-mono text-sky-300">{item.nome}</span>
          {item.argumentos ? (
            <span className="min-w-0 flex-1 truncate font-mono text-zinc-500">
              {item.argumentos}
            </span>
          ) : null}
          {!item.saida ? <span className="shrink-0 text-zinc-700">sem saída</span> : null}
        </summary>
        {item.saida ? (
          <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-zinc-950/60 p-2 font-mono text-[11px] leading-relaxed text-zinc-400">
            {item.saida}
          </pre>
        ) : null}
      </details>
    );
  }

  if (item.tipo === 'ferramenta') {
    const cartao = (
      <details className="group rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-xs">
        <summary className="flex cursor-pointer items-baseline gap-1.5 text-zinc-400 pointer-coarse:min-h-11 pointer-coarse:items-center">
          {/* O <details> abre e fecha por CSS, sem estado no React: então os
              dois ícones ficam montados e o `group-open` esconde um de cada
              vez. Um ícone só, virado por classe, ficaria parado no fechado. */}
          <IconeFechado tamanho={14} className="self-center text-zinc-600 group-open:hidden" />
          <IconeAbrir tamanho={14} className="hidden self-center text-zinc-600 group-open:block" />
          <span className="shrink-0 text-zinc-300">{item.nome}</span>
          <span className="min-w-0 flex-1 truncate font-mono text-zinc-500" title={item.detalhe}>
            {item.resumo}
          </span>
        </summary>
        {item.detalhe ? (
          // `whitespace-pre-wrap` porque o resumo achata as quebras de linha e
          // o detalhe existe justamente para mostrar o comando como ele é.
          <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-zinc-950/60 p-2 font-mono text-[11px] leading-relaxed text-zinc-400">
            {item.detalhe}
          </pre>
        ) : null}
      </details>
    );

    /*
      A miniatura fica FORA do `<details>`, e não dentro.

      Dentro, ela só apareceria depois de a pessoa expandir o cartão — e o
      cartão nasce fechado. Quem acompanha a sessão quer ver de relance qual
      imagem ela leu; abrir para descobrir isso é o mesmo trabalho de antes,
      quando só havia o caminho. O que continua escondido é a entrada crua da
      ferramenta, que é detalhe.
    */
    /*
      O arquivo que a SESSÃO mandou vem pelo mesmo lugar, e pelo mesmo motivo:
      é o que a pessoa quer ver de relance. Fora do `<details>` também porque
      ele carrega o botão de baixar, e botão que decide não mora dentro de
      área que precisa ser aberta primeiro.
    */
    // Sem `sessaoId` não há como remontar a chave do objeto — a rota de
    // leitura a monta a partir dele. Nesse caso sobra o cartão comum, que já
    // diz o nome do arquivo: menos do que se queria, nunca um botão morto.
    if (item.anexo && sessaoId) {
      return (
        <div>
          {cartao}
          <AnexoDaSessao sessaoId={sessaoId} anexo={item.anexo} />
        </div>
      );
    }

    if (!item.imagem) return cartao;
    return (
      <div>
        {cartao}
        <ImagemDaFerramenta caminho={item.imagem} sessaoId={sessaoId} maquinaId={maquinaId} />
      </div>
    );
  }

  if (item.tipo === 'erro-do-motor') {
    /*
      ★ Vermelho, fora de `<details>`, e com a mensagem do motor INTACTA.

      Em 2026-09-21 a cota do Codex acabou e as sessões do dono pararam de
      responder em silêncio: mensagem entrava, resposta nunca vinha, tela muda.
      O motor tinha dito o motivo — com o link para comprar crédito e a hora em
      que a cota volta. Este cartão é o lugar onde ele chega.

      Fora de `<details>` pela mesma razão do aviso de troca de modelo: é um
      fato que explica o que ACABOU de não acontecer, e abrir um cartão para
      descobrir por que a sessão emudeceu é o mesmo silêncio, só que educado.

      **O texto não é reescrito, e isso contraria de propósito o hábito da casa
      de decidir a frase pelo código do erro** (`.claude/rules/web.md`). Aquela
      regra vale para os erros da NOSSA API, cujos códigos nós definimos. Aqui
      o código é da OpenAI: mapear `usageLimitExceeded` para uma frase nossa
      funcionaria hoje e mentiria no dia em que a razão mudasse — um código
      novo cairia no texto genérico e a conversa voltaria a não dizer o que
      houve. O que é nosso é a moldura em pt-BR; o miolo é do motor.

      `whitespace-pre-wrap break-words` porque a mensagem tem URL: sem o
      `break-words` ela estoura a coluna do celular e traz rolagem lateral.
    */
    return (
      <div
        data-testid="erro-do-motor"
        role="status"
        className="flex items-start gap-2 rounded-lg border border-red-800/60 bg-red-950/30 px-3 py-2 text-xs text-red-200"
      >
        <IconeAviso tamanho={14} className="mt-0.5 shrink-0 text-red-400" />
        <div className="min-w-0">
          <p className="font-medium">O motor recusou este turno e não respondeu:</p>
          <p className="mt-1 whitespace-pre-wrap break-words text-red-100/90">{item.mensagem}</p>
          {item.cortada ? (
            <p className="mt-1 text-red-300/70">A mensagem do motor era maior e foi cortada aqui.</p>
          ) : null}
          {item.codigo ? (
            <p className="mt-1 font-mono text-[11px] text-red-300/70">{item.codigo}</p>
          ) : null}
        </div>
      </div>
    );
  }

  if (item.tipo === 'troca-de-modelo') {
    /*
      Âmbar e fora de `<details>`: é um fato que muda o que vem depois (outro
      modelo, outro custo, outro raciocínio), e quem conversa precisa vê-lo de
      relance — não abrir para descobrir. Sem o inglês cru do CLI.
    */
    return (
      <div
        data-testid="troca-de-modelo"
        role="status"
        className="flex items-start gap-2 rounded-lg border border-amber-700/40 bg-amber-950/20 px-3 py-2 text-xs text-amber-200"
      >
        <IconeAviso tamanho={14} className="mt-0.5 shrink-0 text-amber-400" />
        <span className="min-w-0">
          O modelo trocou sozinho: de <strong>{item.de}</strong> para <strong>{item.para}</strong>.
          {item.motivo ? (
            <span className="block text-amber-300/70">Motivo: {item.motivo}.</span>
          ) : null}
        </span>
      </div>
    );
  }

  if (item.tipo === 'modelo-trocado') {
    /*
      A API trocou o modelo desta conversa porque o provedor dele saiu da
      frota (F2, D46). DISCRETO, ao contrário da troca do CLI logo acima: aqui
      nada falhou — a conversa segue, por decisão do dono —, e um cartão âmbar
      a cada conversa antiga reaberta viraria ruído. Mas fora de `<details>`:
      é o que explica por que o próximo turno responde noutro modelo e com
      outro custo, e abrir para descobrir isso é o mesmo silêncio, só que
      educado.

      Os ids vão CRUS (id feio é melhor que nome inventado), e o motivo vai
      como a API o escreveu: é ela quem sabe por que trocou.
      `break-all` porque id de modelo é uma palavra só, e no telefone ele
      estouraria a coluna.
    */
    return (
      <div
        data-testid="modelo-trocado"
        role="status"
        className="flex items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-xs text-zinc-400"
      >
        <IconeTrocaDeModelo tamanho={14} className="mt-0.5 shrink-0 text-zinc-500" />
        <span className="min-w-0">
          O modelo mudou de <strong className="break-all font-mono font-normal text-zinc-200">{item.de}</strong>{' '}
          para <strong className="break-all font-mono font-normal text-zinc-200">{item.para}</strong>.
          {item.motivo ? (
            <span data-testid="motivo-da-troca" className="block text-zinc-500">
              Motivo: {item.motivo}.
            </span>
          ) : null}
        </span>
      </div>
    );
  }

  if (item.tipo === 'custo-do-turno') {
    /*
      Discreto de propósito: é o rodapé de um turno, não um aviso. Cinza, letra
      pequena, alinhado à esquerda como a resposta que ele fecha — e em TEXTO,
      nunca só ícone, porque o número é a informação.
    */
    const { custo, explicacao, visao } = textoDoTurno(item);
    return (
      <p data-testid="fim-do-turno" className="text-[11px] text-zinc-600">
        {custo ? (
          <span data-testid="custo-do-turno" title={explicacao}>
            {custo}
          </span>
        ) : null}
        {custo && item.assinatura ? (
          <SeloAssinatura assinatura={item.assinatura} className="ml-1 align-middle" />
        ) : null}
        {custo && visao ? ' · ' : null}
        {visao ? (
          <span
            data-testid="imagem-lida-por"
            title="Este turno trazia imagem, e foi lido pelo modelo de visão da frota. A sessão continua no modelo dela."
          >
            {visao}
          </span>
        ) : null}
      </p>
    );
  }

  return <p className="text-center text-xs text-zinc-600">{item.texto}</p>;
}

/**
 * Uma pergunta do Claude, com as opções de verdade — não mais JSON cortado em
 * 200 caracteres com dois botões errados. O "Outro" é obrigação nossa: a doc
 * da ferramenta manda o host oferecê-lo sempre.
 *
 * **Uma pergunta por vez.** A ferramenta aceita quatro perguntas com quatro
 * opções cada, e cada opção pode trazer descrição e prévia. Empilhando tudo, o
 * cartão media 3104 px num telefone de 844 px — o botão de responder ficava
 * 2363 px abaixo da dobra, e a conversa sobrava com 19% da tela. Não é "ficou
 * grande": era impossível responder. Agora o paginador mostra uma pergunta, o
 * miolo tem teto e rola sozinho, e o botão de agir mora num rodapé fixo, que
 * nunca sai da vista.
 */
function CartaoDePergunta({
  perguntas,
  aoDecidir,
  ocupado,
}: {
  perguntas: PerguntaDoCartao[];
  aoDecidir: (resposta: DecisaoPermissao) => void;
  ocupado: boolean;
}): React.JSX.Element {
  const [escolhas, setEscolhas] = useState<Record<string, string[]>>({});
  const [livres, setLivres] = useState<Record<string, string>>({});
  const [pagina, setPagina] = useState(0);
  const miolo = useRef<HTMLDivElement>(null);

  /**
   * Trocar de pergunta volta ao topo.
   *
   * O miolo rola, e a rolagem é do elemento, não da pergunta: sem zerar, quem
   * desceu até a quarta opção da primeira pergunta caía no meio da segunda,
   * com o enunciado dela fora da vista. Dá para não perceber que a pergunta
   * mudou.
   */
  function irPara(indice: number): void {
    setPagina(indice);
    if (miolo.current) miolo.current.scrollTop = 0;
  }

  function alternar(pergunta: PerguntaDoCartao, rotulo: string, posicao: number): void {
    setEscolhas((atual) => {
      const marcadas = atual[pergunta.pergunta] ?? [];
      if (!pergunta.multipla) return { ...atual, [pergunta.pergunta]: [rotulo] };
      return {
        ...atual,
        [pergunta.pergunta]: marcadas.includes(rotulo)
          ? marcadas.filter((m) => m !== rotulo)
          : [...marcadas, rotulo],
      };
    });

    /*
      Escolher a resposta já leva para a próxima pergunta.

      Só na escolha única: na múltipla a pessoa ainda pode marcar outras, e no
      texto livre ela está digitando — avançar em qualquer um dos dois tiraria
      a pergunta debaixo da mão. Na última, fica onde está: o passo seguinte é
      o botão de responder, que está logo ali no rodapé.
    */
    if (!pergunta.multipla && posicao < perguntas.length - 1) irPara(posicao + 1);
  }

  // Texto livre vence a seleção na resposta simples; na múltipla, soma.
  function respostaDe(pergunta: PerguntaDoCartao): string {
    const marcadas = escolhas[pergunta.pergunta] ?? [];
    const livre = (livres[pergunta.pergunta] ?? '').trim();
    if (!livre) return marcadas.join(', ');
    return pergunta.multipla && marcadas.length > 0 ? [...marcadas, livre].join(', ') : livre;
  }

  const faltando = perguntas.filter((p) => respostaDe(p).length === 0);
  const completa = faltando.length === 0;
  // A lista pode encolher entre renders (pedido substituído por outro): sem o
  // limite, a página guardada apontaria para uma pergunta que não existe mais.
  const indice = Math.min(pagina, perguntas.length - 1);
  const pergunta = perguntas[indice] as PerguntaDoCartao;
  const varias = perguntas.length > 1;

  return (
    <div className="flex min-h-0 flex-col rounded-lg border border-sky-800 bg-sky-950/20">
      {varias ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-sky-900/60 px-3 py-1.5">
          <span className="text-xs text-sky-300">
            Pergunta {indice + 1} de {perguntas.length}
          </span>
          {/*
            Um ponto por pergunta: é o que diz, sem contar, quantas ainda
            faltam. Cheio = respondida; o de fora marcado = a da vez.
          */}
          <span className="flex min-w-0 flex-1 items-center gap-1" aria-hidden>
            {perguntas.map((p, i) => (
              <span
                key={p.pergunta}
                className={
                  'h-1.5 w-1.5 rounded-full ' +
                  (respostaDe(p).length > 0 ? 'bg-sky-400' : 'bg-zinc-700') +
                  (i === indice ? ' ring-2 ring-sky-400/40' : '')
                }
              />
            ))}
          </span>
          <button
            type="button"
            onClick={() => irPara(indice - 1)}
            disabled={indice === 0}
            aria-label="Pergunta anterior"
            title="Pergunta anterior"
            className="rounded p-1 text-zinc-400 transition hover:bg-sky-900/40 hover:text-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <IconeAnterior tamanho={14} />
          </button>
          <button
            type="button"
            onClick={() => irPara(indice + 1)}
            disabled={indice === perguntas.length - 1}
            aria-label="Próxima pergunta"
            title="Próxima pergunta"
            className="rounded p-1 text-zinc-400 transition hover:bg-sky-900/40 hover:text-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <IconeProximo tamanho={14} />
          </button>
        </div>
      ) : null}

      {/*
        O miolo é o que rola. O teto é em unidade de tela e não em `rem`: o que
        importa é quanto da tela DAQUELE aparelho o cartão pode tomar, e 20rem
        num telefone é metade da altura dele.
      */}
      <div ref={miolo} className="min-h-0 max-h-[min(20rem,32vh)] flex-1 overflow-y-auto p-3">
        <div role="group" aria-label={pergunta.pergunta}>
          <div className="flex items-baseline gap-2 text-sm text-sky-100">
            {pergunta.chip ? (
              <span className="shrink-0 rounded bg-sky-900/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-300">
                {pergunta.chip}
              </span>
            ) : null}
            <TextoMarkdown texto={pergunta.pergunta} />
          </div>
          <div className="mt-2 space-y-1.5">
            {pergunta.opcoes.map((opcao) => (
              <label
                key={opcao.rotulo}
                className="flex cursor-pointer items-start gap-2 rounded-md border border-zinc-800 bg-zinc-950/40 px-2.5 py-1.5 hover:border-zinc-600"
              >
                <input
                  type={pergunta.multipla ? 'checkbox' : 'radio'}
                  name={pergunta.pergunta}
                  checked={(escolhas[pergunta.pergunta] ?? []).includes(opcao.rotulo)}
                  onChange={() => alternar(pergunta, opcao.rotulo, indice)}
                  className="mt-0.5 accent-sky-400"
                />
                <span className="min-w-0 text-sm">
                  <span className="text-zinc-100">{opcao.rotulo}</span>
                  {opcao.descricao ? (
                    <span className="block text-xs text-zinc-500">{opcao.descricao}</span>
                  ) : null}
                  {opcao.previa ? (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs text-zinc-600 hover:text-zinc-400">
                        prévia
                      </summary>
                      <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-zinc-950/70 p-2 text-[11px] text-zinc-400">
                        {opcao.previa}
                      </pre>
                    </details>
                  ) : null}
                </span>
              </label>
            ))}
            <input
              /*
                `password` quando a questão é secreta: o que a pessoa digita
                aqui é token ou senha, e mostrar na tela de uma conversa que
                fica aberta ao lado é o começo do problema — o resto é a
                redação que impede o valor de virar linha de banco.
              */
              type={pergunta.secreta ? 'password' : 'text'}
              value={livres[pergunta.pergunta] ?? ''}
              onChange={(e) =>
                setLivres((atual) => ({ ...atual, [pergunta.pergunta]: e.target.value }))
              }
              placeholder={
                // Sem opções não há "outro": o campo É a resposta.
                pergunta.aberta ? 'Escreva a sua resposta…' : 'Outro — escreva a sua resposta…'
              }
              aria-label={pergunta.pergunta}
              className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-2.5 py-1.5 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-zinc-500"
            />
          </div>
        </div>
      </div>

      {/*
        Rodapé fixo, fora da rolagem: o botão de agir é a única coisa do cartão
        que nunca pode depender de rolar até o fim para aparecer.
      */}
      <div className="flex shrink-0 items-center gap-2 border-t border-sky-900/60 p-3">
        {varias && !completa ? (
          <button
            type="button"
            onClick={() => {
              const proxima = perguntas.findIndex((p) => respostaDe(p).length === 0);
              if (proxima >= 0) irPara(proxima);
            }}
            className="min-w-0 flex-1 truncate text-left text-xs text-zinc-400 underline-offset-2 hover:text-zinc-200 hover:underline"
          >
            {faltando.length === 1
              ? 'falta 1 pergunta — ir para ela'
              : `faltam ${faltando.length} perguntas — ir para a próxima`}
          </button>
        ) : null}
        <button
          onClick={() =>
            aoDecidir({
              decisao: 'responder',
              /*
                A CHAVE da resposta é a do pedido: o Codex chaveia por `id`, o
                Claude pelo enunciado. Mandar sempre o enunciado faria a
                elicitação do Codex chegar com a chave errada — e o formulário
                voltaria vazio para o servidor MCP, sem erro em lugar nenhum.
              */
              respostas: Object.fromEntries(perguntas.map((p) => [p.chave, respostaDe(p)])),
            })
          }
          disabled={ocupado || !completa}
          className={
            'rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-sky-500 disabled:opacity-40 ' +
            (varias && !completa ? 'shrink-0' : 'w-full')
          }
        >
          Responder
        </button>
      </div>
    </div>
  );
}

/** O plano da sessão, com as três respostas do VS Code. */
function CartaoDePlano({
  plano,
  aoDecidir,
  ocupado,
}: {
  plano: string;
  aoDecidir: (resposta: DecisaoPermissao) => void;
  ocupado: boolean;
}): React.JSX.Element {
  const [devolvendo, setDevolvendo] = useState(false);
  const [observacao, setObservacao] = useState('');

  return (
    <div className="rounded-lg border border-emerald-800 bg-emerald-950/20 p-3">
      <p className="text-sm font-medium text-emerald-100">
        A sessão terminou um plano e espera sua aprovação
      </p>
      <div className="mt-2 max-h-[min(22rem,34vh)] overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
        <TextoMarkdown texto={plano} />
      </div>
      {devolvendo ? (
        <div className="mt-3 space-y-2">
          <textarea
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            rows={3}
            placeholder="O que falta no plano?"
            autoFocus
            className="w-full resize-none rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-zinc-500"
          />
          <div className="flex gap-2">
            <button
              onClick={() =>
                aoDecidir({
                  decisao: 'plano',
                  acao: 'continuar_planejando',
                  ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
                })
              }
              disabled={ocupado}
              className="flex-1 rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-50"
            >
              Devolver para o planejamento
            </button>
            <button
              onClick={() => setDevolvendo(false)}
              className="rounded-md px-3 py-2 text-sm text-zinc-500 hover:text-zinc-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <button
            onClick={() => aoDecidir({ decisao: 'plano', acao: 'aceitar_edicoes' })}
            disabled={ocupado}
            className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-emerald-500 disabled:opacity-50"
          >
            Aprovar e aceitar edições
          </button>
          <button
            onClick={() => aoDecidir({ decisao: 'plano', acao: 'perguntar_sempre' })}
            disabled={ocupado}
            className="rounded-md border border-emerald-700 px-3 py-2 text-sm text-emerald-100 transition hover:bg-emerald-900/40 disabled:opacity-50"
          >
            Aprovar e perguntar a cada passo
          </button>
          <button
            onClick={() => setDevolvendo(true)}
            disabled={ocupado}
            className="rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-50"
          >
            Continuar planejando
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * O formulário de uma elicitação MCP (E23).
 *
 * Quem pede não é a sessão: é um servidor MCP que ela consultou, e a thread do
 * Codex fica PARADA até alguém responder. Por isso o cabeçalho diz o nome do
 * servidor — "a sessão quer" mandaria procurar no lugar errado.
 *
 * Nenhum controle nativo: escolha é `CampoDeEscolha`, marcação é
 * `CaixaDeMarcar`, e o que é multi vira uma caixa por opção. A regra da casa
 * vale aqui como em toda tela.
 */
function CartaoDeFormulario({
  servidor,
  mensagem,
  campos,
  aoDecidir,
  ocupado,
}: {
  servidor: string;
  mensagem: string;
  campos: CampoDoFormulario[];
  aoDecidir: (resposta: DecisaoPermissao) => void;
  ocupado: boolean;
}): React.JSX.Element {
  const [valores, setValores] = useState<Record<string, string>>(() =>
    Object.fromEntries(campos.map((c) => [c.chave, c.padrao ?? ''])),
  );

  const faltando = campos.filter((c) => c.obrigatorio && !valores[c.chave]?.trim());

  function alternarMulti(chave: string, valor: string): void {
    const atual = (valores[chave] ?? '').split(', ').filter(Boolean);
    const novo = atual.includes(valor)
      ? atual.filter((x) => x !== valor)
      : [...atual, valor];
    setValores({ ...valores, [chave]: novo.join(', ') });
  }

  return (
    <div className="rounded-lg border border-sky-900 bg-sky-950/20 p-3" data-teste="cartao-elicitacao">
      <p className="text-sm text-sky-100">
        <span className="font-medium">{servidor || 'Um servidor MCP'}</span> pede dados para
        continuar
      </p>
      {mensagem ? <p className="mt-1 text-xs text-zinc-300">{mensagem}</p> : null}

      <div className="mt-3 space-y-3">
        {campos.map((campo) => (
          <div key={campo.chave} className="space-y-1">
            {campo.tipo === 'escolha' ? (
              <CampoDeEscolha
                rotulo={campo.rotulo}
                valor={valores[campo.chave] || null}
                opcoes={campo.opcoes.map((o) => ({
                  // O id é o VALOR que vai para o servidor; o rótulo é só para ler.
                  id: o.valor ?? o.rotulo,
                  rotulo: o.rotulo,
                  ...(o.descricao ? { descricao: o.descricao } : {}),
                }))}
                aoEscolher={(id) => setValores({ ...valores, [campo.chave]: id })}
              />
            ) : campo.tipo === 'booleano' ? (
              <CaixaDeMarcar
                marcada={valores[campo.chave] === 'true'}
                aoMarcar={(m) => setValores({ ...valores, [campo.chave]: m ? 'true' : 'false' })}
              >
                {campo.rotulo}
              </CaixaDeMarcar>
            ) : campo.tipo === 'multipla' ? (
              <>
                <span className="block text-xs text-zinc-400">{campo.rotulo}</span>
                {campo.opcoes.map((o) => (
                  <CaixaDeMarcar
                    key={o.valor ?? o.rotulo}
                    marcada={(valores[campo.chave] ?? '').split(', ').includes(o.valor ?? o.rotulo)}
                    aoMarcar={() => alternarMulti(campo.chave, o.valor ?? o.rotulo)}
                  >
                    {o.rotulo}
                  </CaixaDeMarcar>
                ))}
              </>
            ) : (
              <label className="block space-y-1">
                <span className="block text-xs text-zinc-400">
                  {campo.rotulo}
                  {campo.obrigatorio ? ' *' : ''}
                </span>
                <input
                  type={campo.tipo === 'numero' ? 'number' : 'text'}
                  value={valores[campo.chave] ?? ''}
                  onChange={(e) => setValores({ ...valores, [campo.chave]: e.target.value })}
                  className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-zinc-100"
                />
              </label>
            )}
            {campo.descricao ? (
              <span className="block text-[11px] text-zinc-500">{campo.descricao}</span>
            ) : null}
          </div>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        <button
          onClick={() => aoDecidir({ decisao: 'responder', respostas: valores })}
          disabled={ocupado || faltando.length > 0}
          className="rounded-md bg-sky-200 px-3 py-2 text-sm font-medium text-sky-950 transition hover:bg-sky-100 disabled:opacity-50"
        >
          Enviar
        </button>
        <button
          onClick={() => aoDecidir({ decisao: 'negar', mensagem: 'Recusado pelo painel', interromper: false })}
          disabled={ocupado}
          className="rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-50"
        >
          Recusar
        </button>
      </div>
    </div>
  );
}

/**
 * Elicitação por LINK: o servidor pede que a pessoa abra uma página.
 *
 * O aviso não é decoração. O endereço vem de um servidor MCP — conteúdo de
 * terceiro, como tudo que as ferramentas devolvem —, e o cartão mostra o
 * endereço INTEIRO em vez de esconder num rótulo bonito: quem clica precisa
 * ver para onde vai.
 */
function CartaoDeLink({
  servidor,
  mensagem,
  url,
  aoDecidir,
  ocupado,
}: {
  servidor: string;
  mensagem: string;
  url: string;
  aoDecidir: (resposta: DecisaoPermissao) => void;
  ocupado: boolean;
}): React.JSX.Element {
  return (
    <div className="rounded-lg border border-sky-900 bg-sky-950/20 p-3" data-teste="cartao-elicitacao-url">
      <p className="text-sm text-sky-100">
        <span className="font-medium">{servidor || 'Um servidor MCP'}</span> pede que você abra uma
        página
      </p>
      {mensagem ? <p className="mt-1 text-xs text-zinc-300">{mensagem}</p> : null}
      <p className="mt-2 flex items-start gap-1.5 rounded bg-amber-950/40 px-2 py-1.5 text-xs text-amber-200">
        <IconeAviso tamanho={14} className="mt-px" />
        <span className="min-w-0">
          O endereço veio de um servidor externo. Confira para onde ele leva antes de abrir.
        </span>
      </p>
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        className="mt-2 block break-all rounded bg-zinc-950/60 px-3 py-2 font-mono text-xs text-sky-300 underline"
      >
        {url}
      </a>
      <div className="mt-3 flex gap-2">
        <button
          onClick={() => aoDecidir({ decisao: 'responder', respostas: { abriu: 'sim' } })}
          disabled={ocupado}
          className="rounded-md bg-sky-200 px-3 py-2 text-sm font-medium text-sky-950 transition hover:bg-sky-100 disabled:opacity-50"
        >
          Já abri, pode seguir
        </button>
        <button
          onClick={() => aoDecidir({ decisao: 'negar', mensagem: 'Recusado pelo painel', interromper: false })}
          disabled={ocupado}
          className="rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-50"
        >
          Recusar
        </button>
      </div>
    </div>
  );
}

/**
 * A aprovação de chamada de ferramenta MCP, que chega disfarçada de elicitação.
 *
 * Medido em M0-9, com turno de verdade: o Codex manda
 * `mcpServer/elicitation/request` com `requestedSchema` VAZIO, a pergunta em
 * inglês no `message` e a verdade em `_meta.codex_approval_kind`. Sem este
 * cartão o pedido caía no genérico, cujo botão Permitir manda
 * `decisao: 'permitir'` — que um pedido ELICITACAO recusa com 422. O dono via,
 * clicava, e recebia erro.
 *
 * Desde a revisão do PR 58 (2.1.3) o daemon a manda como FERRAMENTA, e o
 * Permitir é `permitir`. Um daemon anterior a mandava como ELICITACAO — aí o
 * Permitir responde como elicitação (`responder` vazio é o `accept` do MCP).
 * `negar` vale nos dois.
 */
function CartaoDeAprovacaoMcp({
  servidor,
  ferramenta,
  descricao,
  argumentos,
  pedeResposta,
  aoDecidir,
  ocupado,
}: {
  servidor: string;
  ferramenta: string;
  descricao: string;
  argumentos: string;
  pedeResposta: boolean;
  aoDecidir: (resposta: DecisaoPermissao) => void;
  ocupado: boolean;
}): React.JSX.Element {
  return (
    <div className="rounded-lg border border-amber-800 bg-amber-950/30 p-3" data-teste="cartao-aprovacao-mcp">
      {/* Em português, e dizendo QUEM quer o quê: a pergunta do Codex vem em
          inglês, e o painel não mostra texto cru de servidor. */}
      <p className="text-sm text-amber-100">
        A sessão quer usar <span className="font-medium">{ferramenta || 'uma ferramenta'}</span> do
        servidor {servidor || 'MCP'}
      </p>
      {descricao ? <p className="mt-0.5 text-xs text-zinc-400">{descricao}</p> : null}
      {argumentos ? (
        <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-zinc-950/60 px-3 py-2 text-xs text-zinc-300">
          {argumentos}
        </pre>
      ) : null}
      <div className="mt-3 flex gap-2">
        <button
          onClick={() =>
            aoDecidir(pedeResposta ? { decisao: 'responder', respostas: {} } : { decisao: 'permitir', lembrarNaSessao: false })
          }
          disabled={ocupado}
          className="rounded-md bg-amber-200 px-3 py-2 text-sm font-medium text-amber-950 transition hover:bg-amber-100 disabled:opacity-50"
        >
          Permitir
        </button>
        <button
          onClick={() => aoDecidir({ decisao: 'negar', mensagem: 'Negado pelo painel', interromper: false })}
          disabled={ocupado}
          className="rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-50"
        >
          Negar
        </button>
      </div>
    </div>
  );
}

/**
 * O pedido de permissão do OpenCode (F17): caminho, diff e padrão (OC-10).
 *
 * Três botões, e não a caixa "não perguntar de novo" do Claude: no OpenCode
 * lembrar é uma RESPOSTA própria (`always`, Interface congelada item 15), que
 * vale para os padrões do pedido até a sessão acabar — e ela existe em todo
 * pedido, não só quando o CLI mandou sugestões. Um botão diz isso de uma vez;
 * uma caixa marcada antes do Permitir pediria dois gestos para uma decisão.
 */
function CartaoDoOpencode({
  pedido,
  cartao,
  aoDecidir,
  ocupado,
  aoAbrirArquivo,
}: {
  pedido: Pedido;
  cartao: Extract<CartaoDePedido, { tipo: 'opencode' }>;
  aoDecidir: (resposta: DecisaoPermissao) => void;
  ocupado: boolean;
  aoAbrirArquivo?: (caminho: string) => void;
}): React.JSX.Element {
  // O diff chega unificado; o editor de comparação quer os dois lados. Sem
  // `@@` ele não é separável, e aí aparece como veio — feio, mas inteiro.
  const lados = cartao.diff ? ladosDoDiffUnificado(cartao.diff) : null;
  return (
    <div className="rounded-lg border border-amber-800 bg-amber-950/30 p-3" data-teste="cartao-opencode">
      <p className="text-sm text-amber-100">
        {fraseDoPedido(pedido.ferramenta, pedido.entrada, 'OPENCODE')}
      </p>
      {cartao.arquivo ? (
        <p className="mt-1 break-all font-mono text-xs text-zinc-400" data-teste="caminho-do-pedido">
          {cartao.arquivo}
        </p>
      ) : null}
      {lados && cartao.arquivo ? (
        <div className="mt-2">
          <CartaoDeEdicao
            item={{
              chave: 'pedido-' + pedido.id,
              tipo: 'edicao',
              arquivo: cartao.arquivo,
              ...contarLinhas(lados.antes, lados.depois),
              antes: lados.antes.slice(0, 20_000),
              depois: lados.depois.slice(0, 20_000),
              criacao: false,
            }}
            aoAbrirArquivo={aoAbrirArquivo}
            diffPreso
          />
        </div>
      ) : cartao.diff ? (
        <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-zinc-950/60 px-3 py-2 text-xs text-zinc-300">
          {cartao.diff.slice(0, 20_000)}
        </pre>
      ) : null}
      {cartao.padroes.length > 0 ? (
        <div className="mt-2 text-xs" data-teste="padroes-do-pedido">
          <p className="text-zinc-500">
            {/* O padrão é o que "sempre" passa a liberar: a pessoa lê antes de decidir. */}
            {cartao.padroes.length === 1 ? 'Padrão pedido:' : 'Padrões pedidos:'}
          </p>
          <ul className="mt-0.5 max-h-24 space-y-0.5 overflow-auto">
            {cartao.padroes.map((padrao) => (
              <li
                key={padrao}
                className="whitespace-pre-wrap break-all rounded bg-zinc-950/60 px-2 py-1 font-mono text-zinc-300"
              >
                {padrao}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {cartao.arquivo && aoAbrirArquivo ? (
        <button
          onClick={() => aoAbrirArquivo(cartao.arquivo as string)}
          className="mt-2 text-xs text-amber-200 underline hover:text-amber-100"
        >
          ver este arquivo antes de decidir
        </button>
      ) : null}
      {cartao.sempre.length > 0 ? (
        /*
          O que o "sempre" libera pode ser MAIS LARGO que o pedido (o OpenCode
          manda `git status` no padrão e `git status *` no `always`). Quem
          aperta o botão precisa ler isso antes, e não descobrir depois.
        */
        <p className="mt-2 break-all text-xs text-zinc-500" data-teste="sempre-libera">
          «sempre» libera até o fim da sessão:{' '}
          <span className="font-mono text-zinc-300">{cartao.sempre.join(' · ')}</span>
        </p>
      ) : null}
      {/* `flex-wrap`: três botões não cabem lado a lado em 390 px, e o que
          passa da borda some sem barra nenhuma avisando. */}
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={() => aoDecidir({ decisao: 'permitir', lembrarNaSessao: false })}
          disabled={ocupado}
          className="flex-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-emerald-500 disabled:opacity-50"
        >
          Permitir
        </button>
        <button
          onClick={() => aoDecidir({ decisao: 'permitir', lembrarNaSessao: true })}
          disabled={ocupado}
          title="Libera este padrão até a sessão acabar"
          className="flex-1 whitespace-nowrap rounded-md border border-emerald-700 px-3 py-2 text-sm text-emerald-200 transition hover:bg-emerald-950 disabled:opacity-50"
        >
          Permitir sempre nesta sessão
        </button>
        <button
          onClick={() => aoDecidir({ decisao: 'negar', mensagem: 'Negado pelo painel', interromper: false })}
          disabled={ocupado}
          className="flex-1 rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-50"
        >
          Negar
        </button>
      </div>
    </div>
  );
}

/**
 * O cartão de EXECUÇÃO do toolkit (F3.7, ADR-0026): o que a pessoa autoriza é
 * UMA versão de UMA rotina, num alvo, numa fase — e o cartão diz as seis
 * coisas por extenso, com o hash curto da versão. O hash é o que prende a
 * autorização ao que vai rodar: um byte a mais no pacote é outra versão, e
 * outro cartão.
 *
 * O miolo rola e o rodapé fica (`web.md`: cartão com ação tem rodapé fixo):
 * vinte parâmetros não podem empurrar o Permitir para fora da vista. Mesmo
 * desenho na mestre — quem pede é o executor do daemon, e não a sessão.
 */
function CartaoDoToolkit({
  cartao,
  aoDecidir,
  ocupado,
}: {
  cartao: Extract<CartaoDePedido, { tipo: 'toolkit' }>;
  aoDecidir: (resposta: DecisaoPermissao) => void;
  ocupado: boolean;
}): React.JSX.Element {
  const conhecida = classeConhecida(cartao.classe);
  // Classe desconhecida é o pior caso: o aviso aparece como se fosse ato.
  const sai = conhecida === null || conhecida === 'ATO_EXTERNO';
  const curto = hashCurto(cartao.sha256);
  return (
    <div
      className="flex min-h-0 flex-col rounded-lg border border-amber-800 bg-amber-950/30"
      data-teste="cartao-toolkit"
    >
      <div className="min-h-0 max-h-[min(22rem,34vh)] flex-1 overflow-y-auto p-3">
        <p className="text-sm text-amber-100">A sessão quer rodar uma rotina do toolkit</p>
        <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-1.5 text-xs">
          <dt className="text-zinc-500">rotina</dt>
          <dd className="min-w-0 break-all font-mono text-zinc-100" data-teste="toolkit-rotina">
            {cartao.rotina}
            {cartao.versao !== null ? <span className="ml-1.5 text-zinc-400">v{cartao.versao}</span> : null}
          </dd>
          <dt className="text-zinc-500">alvo</dt>
          <dd className="min-w-0 break-all font-mono text-zinc-200" data-teste="toolkit-alvo">
            {cartao.alvo}
          </dd>
          <dt className="text-zinc-500">estado no alvo</dt>
          <dd className="min-w-0">
            <SeloDoEstado estado={cartao.estadoNoAlvo} />
          </dd>
          <dt className="text-zinc-500">fase</dt>
          <dd className="min-w-0 break-all font-mono text-zinc-200" data-teste="toolkit-fase">
            {cartao.fase}
          </dd>
          <dt className="text-zinc-500">classe</dt>
          <dd className="min-w-0">
            <SeloDaClasse classe={cartao.classe} />
          </dd>
          <dt className="text-zinc-500">versão</dt>
          <dd className="min-w-0 font-mono text-zinc-200" title={cartao.sha256} data-teste="toolkit-hash">
            {curto}
          </dd>
        </dl>
        {sai ? (
          <p className="mt-2 flex items-start gap-1.5 rounded bg-red-950/60 px-2 py-1.5 text-xs font-medium text-red-200 ring-1 ring-red-800">
            <IconeAviso tamanho={14} className="mt-px" />
            <span className="min-w-0">
              Ato externo: o efeito sai da máquina (protocolar, assinar, mandar ao cliente). Permitir vale só
              para a versão {curto}.
            </span>
          </p>
        ) : null}
        {cartao.parametros.length > 0 ? (
          <div className="mt-2 text-xs">
            <p className="text-zinc-500">Parâmetros</p>
            <ul className="mt-0.5 space-y-0.5" data-teste="toolkit-parametros">
              {cartao.parametros.map((p) => (
                <li key={p.nome} className="break-all rounded bg-zinc-950/60 px-2 py-1 font-mono text-zinc-300">
                  <span className="text-zinc-500">{p.nome} = </span>
                  {p.valor}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="mt-2 text-xs text-zinc-500">sem parâmetros</p>
        )}
      </div>
      <div className="flex shrink-0 gap-2 border-t border-amber-900/60 p-3">
        <button
          onClick={() => aoDecidir({ decisao: 'permitir', lembrarNaSessao: false })}
          disabled={ocupado}
          className="flex-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-emerald-500 disabled:opacity-50"
        >
          Permitir
        </button>
        <button
          onClick={() => aoDecidir({ decisao: 'negar', mensagem: 'Recusado pelo painel', interromper: false })}
          disabled={ocupado}
          className="flex-1 rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-50"
        >
          Recusar
        </button>
      </div>
    </div>
  );
}

/**
 * A frase do cartão genérico, pelo motor VERDADEIRO do pedido.
 *
 * Motor desconhecido não vira Claude: a frase do Claude descreveria outra
 * coisa, e o nome cru ao menos não mente sobre o que se pede.
 */
function fraseDoCartao(pedido: Pedido): string {
  const motor = motorDoPedido(pedido.motor);
  return motor === null
    ? `Pedido de um motor que este painel não conhece: ${pedido.ferramenta}`
    : fraseDoPedido(pedido.ferramenta, pedido.entrada, motor);
}

/** Cartão de permissão: despacha para o desenho certo pelo tipo do pedido. */
export function CartaoDePermissao({
  pedido,
  aoDecidir,
  ocupado,
  aoAbrirArquivo,
}: {
  pedido: Pedido;
  aoDecidir: (resposta: DecisaoPermissao) => void;
  ocupado: boolean;
  aoAbrirArquivo?: (caminho: string) => void;
}): React.JSX.Element {
  const [lembrar, setLembrar] = useState(false);
  const cartao = interpretarPedido(pedido);

  if (cartao.tipo === 'pergunta') {
    return <CartaoDePergunta perguntas={cartao.perguntas} aoDecidir={aoDecidir} ocupado={ocupado} />;
  }
  if (cartao.tipo === 'plano') {
    return <CartaoDePlano plano={cartao.plano} aoDecidir={aoDecidir} ocupado={ocupado} />;
  }
  if (cartao.tipo === 'formulario') {
    return (
      <CartaoDeFormulario
        servidor={cartao.servidor}
        mensagem={cartao.mensagem}
        campos={cartao.campos}
        aoDecidir={aoDecidir}
        ocupado={ocupado}
      />
    );
  }
  if (cartao.tipo === 'aprovacao-mcp') {
    return (
      <CartaoDeAprovacaoMcp
        servidor={cartao.servidor}
        ferramenta={cartao.ferramenta}
        descricao={cartao.descricao}
        argumentos={cartao.argumentos}
        pedeResposta={cartao.pedeResposta}
        aoDecidir={aoDecidir}
        ocupado={ocupado}
      />
    );
  }
  if (cartao.tipo === 'link') {
    return (
      <CartaoDeLink
        servidor={cartao.servidor}
        mensagem={cartao.mensagem}
        url={cartao.url}
        aoDecidir={aoDecidir}
        ocupado={ocupado}
      />
    );
  }
  if (cartao.tipo === 'toolkit') {
    return <CartaoDoToolkit cartao={cartao} aoDecidir={aoDecidir} ocupado={ocupado} />;
  }
  if (cartao.tipo === 'opencode') {
    return (
      <CartaoDoOpencode
        pedido={pedido}
        cartao={cartao}
        aoDecidir={aoDecidir}
        ocupado={ocupado}
        {...(aoAbrirArquivo ? { aoAbrirArquivo } : {})}
      />
    );
  }

  const temSugestao = (pedido.sugestoes?.length ?? 0) > 0;
  const descricaoDaEntrada =
    typeof pedido.entrada['description'] === 'string' ? pedido.entrada['description'] : '';
  const emSegundoPlano = pedido.entrada['run_in_background'] === true;
  // O que esta ferramenta faz de irreversível, de caro ou de fora da máquina.
  const aviso = avisoDeFerramenta(pedido.ferramenta, pedido.entrada);

  // Edição pede diff, não JSON escapado: é o mesmo desenho do cartão da
  // conversa, no lugar onde a decisão é tomada.
  const antes =
    typeof pedido.entrada['old_string'] === 'string' ? pedido.entrada['old_string'] : '';
  const depois =
    typeof pedido.entrada['new_string'] === 'string'
      ? pedido.entrada['new_string']
      : typeof pedido.entrada['content'] === 'string'
        ? pedido.entrada['content']
        : '';
  const comDiff =
    (pedido.ferramenta === 'Edit' || pedido.ferramenta === 'Write') &&
    cartao.arquivo !== null &&
    depois.length > 0;

  return (
    <div className="rounded-lg border border-amber-800 bg-amber-950/30 p-3">
      {/* O motor VEM do pedido: com `'CLAUDE'` fixo, um pedido do Codex caía
          no genérico e o cartão dizia o nome de um método JSON-RPC no lugar do
          arquivo que vai ser editado. E o ternário que veio depois mandava o
          terceiro motor para as frases do Claude, calado. */}
      <p className="text-sm text-amber-100">{fraseDoCartao(pedido)}</p>
      {descricaoDaEntrada ? (
        <p className="mt-0.5 text-xs text-zinc-400">{descricaoDaEntrada}</p>
      ) : null}
      {emSegundoPlano ? (
        <p className="mt-1 text-[11px]">
          <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-zinc-400">em segundo plano</span>
        </p>
      ) : null}
      {aviso ? (
        <p
          className={
            'mt-2 flex items-start gap-1.5 rounded px-2 py-1.5 text-xs ' +
            (aviso.grave
              ? 'bg-red-950/60 font-medium text-red-200 ring-1 ring-red-800'
              : 'bg-zinc-900/60 text-zinc-400')
          }
        >
          {aviso.grave ? <IconeAviso tamanho={14} className="mt-px" /> : null}
          <span className="min-w-0">{aviso.texto}</span>
        </p>
      ) : null}
      {comDiff ? (
        <div className="mt-2">
          <CartaoDeEdicao
            item={{
              chave: 'pedido-' + pedido.id,
              tipo: 'edicao',
              arquivo: cartao.arquivo as string,
              ...contarLinhas(antes, depois),
              antes: antes.slice(0, 20_000),
              depois: depois.slice(0, 20_000),
              criacao: pedido.ferramenta === 'Write',
            }}
            aoAbrirArquivo={aoAbrirArquivo}
            diffPreso
          />
        </div>
      ) : (
        // `whitespace-pre-wrap break-words`: sem eles o `<pre>` guarda o
        // comando numa linha só e, num telefone, decidir "permitir" virava
        // aprovar o que não dá para ler — o texto saía pela direita da caixa,
        // que fica encostada nos botões.
        <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-zinc-950/60 px-3 py-2 text-xs text-zinc-300">
          {cartao.detalhe}
        </pre>
      )}
      {cartao.arquivo && aoAbrirArquivo ? (
        <button
          onClick={() => aoAbrirArquivo(cartao.arquivo as string)}
          className="mt-2 text-xs text-amber-200 underline hover:text-amber-100"
        >
          ver este arquivo antes de decidir
        </button>
      ) : null}
      {temSugestao ? (
        // A caixa da casa, e não o `<input type="checkbox">` cru que estava
        // aqui: no meio do cartão escuro ele aparecia como o quadrado branco
        // do sistema (`web-controles.md`).
        <CaixaDeMarcar
          marcada={lembrar}
          aoMarcar={setLembrar}
          className="mt-2 text-xs text-zinc-400"
        >
          não perguntar de novo por isto nesta sessão
        </CaixaDeMarcar>
      ) : null}
      <div className="mt-3 flex gap-2">
        <button
          onClick={() => aoDecidir({ decisao: 'permitir', lembrarNaSessao: lembrar && temSugestao })}
          disabled={ocupado}
          className="flex-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-emerald-500 disabled:opacity-50"
        >
          Permitir
        </button>
        <button
          onClick={() =>
            aoDecidir({ decisao: 'negar', mensagem: 'Negado pelo painel', interromper: false })
          }
          disabled={ocupado}
          className="flex-1 rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-50"
        >
          Negar
        </button>
      </div>
    </div>
  );
}

/** O nome do que está esperando, para a barra recolhida dizer o que é. */
function nomeDoPedido(pedido: Pedido): string {
  if (pedido.tipo === 'PERGUNTA') return 'pergunta';
  if (pedido.tipo === 'PLANO') return 'plano';
  return 'pedido';
}

/**
 * A faixa de pedidos pendentes, acima do compositor.
 *
 * Três coisas que ela resolve, e todas nasceram de medir o uso real:
 *
 * - **Um pedido por vez.** Dois pedidos empilhados mediam 2154 px numa janela
 *   de 900. Empilhar é o desenho certo para uma lista de três linhas, não para
 *   cartões que trazem plano, diff e quatro perguntas dentro.
 * - **Teto de altura.** A faixa é `shrink-0` acima de uma conversa `flex-1`:
 *   sem teto ela não empurra a conversa, ela **transborda o painel** e o botão
 *   de agir some para além do recorte, sem barra de rolagem nenhuma para
 *   avisar que ele existe.
 * - **Minimizar.** Quase toda pergunta se responde relendo a conversa, e a
 *   conversa estava atrás do cartão. Minimizar não decide nada: guarda o
 *   pedido numa linha e devolve a tela.
 */
export function PilhaDePedidos({
  pedidos,
  ocupado,
  aoDecidir,
  aoAbrirArquivo,
}: {
  pedidos: Pedido[];
  ocupado: boolean;
  aoDecidir: (pedidoId: string, resposta: DecisaoPermissao) => void;
  aoAbrirArquivo?: (caminho: string) => void;
}): React.JSX.Element | null {
  const [pagina, setPagina] = useState(0);
  const [minimizada, setMinimizada] = useState(false);
  const miolo = useRef<HTMLDivElement>(null);

  /** Trocar de pedido também volta ao topo, pelo mesmo motivo da pergunta. */
  function irPara(indice: number): void {
    setPagina(indice);
    if (miolo.current) miolo.current.scrollTop = 0;
  }

  /*
    Minimizar vale para ESTA espera, não para sempre.

    Sem isto a escolha ficaria colada no componente pelo resto da sessão: a
    pessoa recolhe para ler uma pergunta, responde, e a próxima — feita meia
    hora depois, sobre outro assunto — nasceria recolhida por causa de um
    clique que já tinha cumprido o seu papel.
  */
  useEffect(() => {
    if (pedidos.length === 0) setMinimizada(false);
  }, [pedidos.length]);

  if (pedidos.length === 0) return null;

  const indice = Math.min(pagina, pedidos.length - 1);
  const pedido = pedidos[indice] as Pedido;

  if (minimizada) {
    const rotulo =
      pedidos.length === 1
        ? `1 ${nomeDoPedido(pedido)} esperando`
        : `${pedidos.length} pedidos esperando`;
    return (
      <section
        id="pedidos"
        className="flex shrink-0 items-center gap-2 border-t border-sky-900/60 bg-sky-950/20 px-3 py-2"
      >
        <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-sky-400" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-xs text-sky-200">{rotulo}</span>
        <button
          type="button"
          onClick={() => setMinimizada(false)}
          className="shrink-0 rounded-md border border-sky-800 px-2.5 py-1 text-xs text-sky-200 transition hover:bg-sky-900/40"
        >
          Abrir o pedido
        </button>
      </section>
    );
  }

  return (
    <section
      id="pedidos"
      className="flex max-h-[55vh] shrink-0 flex-col border-t border-zinc-800 p-3"
    >
      <div className="mb-2 flex shrink-0 items-center gap-2">
        {pedidos.length > 1 ? (
          <>
            <span className="text-xs text-zinc-400">
              Pedido {indice + 1} de {pedidos.length}
            </span>
            <button
              type="button"
              onClick={() => irPara(indice - 1)}
              disabled={indice === 0}
              aria-label="Pedido anterior"
              title="Pedido anterior"
              className="rounded p-1 text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <IconeAnterior tamanho={14} />
            </button>
            <button
              type="button"
              onClick={() => irPara(indice + 1)}
              disabled={indice === pedidos.length - 1}
              aria-label="Próximo pedido"
              title="Próximo pedido"
              className="rounded p-1 text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <IconeProximo tamanho={14} />
            </button>
          </>
        ) : (
          <span className="text-xs text-zinc-500">A sessão está esperando você</span>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => setMinimizada(true)}
          aria-label="Minimizar"
          title="Minimizar para ler a conversa — não responde nada"
          className="flex shrink-0 items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-100"
        >
          <IconeMinimizar tamanho={13} />
          Minimizar
        </button>
      </div>

      {/*
        A chave é o id do pedido: trocar de página tem de zerar a seleção de
        rádio e o texto livre do cartão anterior. Sem ela, o React reaproveita
        o estado e a resposta de um pedido apareceria escrita dentro do outro.
      */}
      <div ref={miolo} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <CartaoDePermissao
          key={pedido.id}
          pedido={pedido}
          ocupado={ocupado}
          aoDecidir={(resposta) => aoDecidir(pedido.id, resposta)}
          {...(aoAbrirArquivo ? { aoAbrirArquivo } : {})}
        />
      </div>
    </section>
  );
}
